// Chat — one inbox for the store, keyed by the PERSON, not the receipt.
//
// Every thread is a contact (public.contacts, one row per phone per store).
// Today the messages in a thread are the receipt texts (the SMS AI, the
// customer and staff replies) across every receipt that person has had.
// Website designer chats and sales-desk chats land in the same threads next
// (they will carry contact_id with no receipt_id). The list comes from the
// contact_threads view: newest activity first, unread count, latest receipt.
//
// Replies go out the same way the Orders drawer sends a text
// (/api/sms/send-message with the latest receipt for ownership + STOP
// check; the endpoint saves the staff message, the trigger stamps the
// contact). "AI replying / You are replying" flips is_ai_handling on the
// person's receipts, the same flag the SMS AI already honours.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Bot, Check, FileText, Globe, Info, Loader2, MessageSquare,
  PhoneCall, RefreshCw, Search, Send, Store, Upload, User, UserPlus, X,
} from 'lucide-react';
import { supabase, authHeaders, sendMessage } from '../supabaseClient';

// ---------- small helpers ----------
const digits = (s) => String(s || '').replace(/\D/g, '');
const prettyPhone = (s) => {
  const d = digits(s).replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(s || '');
};
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : '2-digit' });
};
const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const days = Math.round((today - that) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};
const clock = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const SOURCES = {
  online_order: { label: 'Online order', Icon: Globe, cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  website_chat: { label: 'Website chat', Icon: Globe, cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  sales_desk:   { label: 'Sales desk', Icon: MessageSquare, cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  receipt:      { label: 'In store', Icon: Store, cls: 'text-slate-600 bg-slate-50 border-slate-200' },
  pos_import:   { label: 'Register', Icon: Upload, cls: 'text-slate-600 bg-slate-50 border-slate-200' },
  signup:       { label: 'Sign-up', Icon: UserPlus, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  phone_call:   { label: 'Phone call', Icon: PhoneCall, cls: 'text-sky-700 bg-sky-50 border-sky-200' },
  sms:          { label: 'Text', Icon: MessageSquare, cls: 'text-slate-600 bg-slate-50 border-slate-200' },
};
const sourceOf = (key) => SOURCES[key] || { label: key ? key.replace(/_/g, ' ') : 'Contact', Icon: User, cls: 'text-slate-600 bg-slate-50 border-slate-200' };
const viaLabel = (via) => ({ sms: 'Text', web: 'Web', phone_agent: 'Phone', test: 'Test' })[via] || (via || '');

const STATUS_LABEL = {
  pending: 'Open', out_for_delivery: 'On truck', partial_out_for_delivery: 'Part on truck',
  partially_delivered: 'Part delivered', delivered: 'Delivered', picked_up: 'Picked up', cancelled: 'Cancelled',
};

const initials = (t) => {
  const name = String(t || '').trim();
  if (!name) return '?';
  if (/^[\d+()\s-]+$/.test(name)) return '#';
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'human', label: 'You replying' },
  { id: 'online', label: 'Online' },
  { id: 'stop', label: 'Stopped' },
];

const SourceChip = ({ source, className = '' }) => {
  const { label, Icon, cls } = sourceOf(source);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls} ${className}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

// ---------- the tab ----------
export default function ChatTab({ storeId, store, addNotification, onOpenReceipt, hideHeader = false }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [togglingAi, setTogglingAi] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const selectedRef = useRef(null);
  const listEndRef = useRef(null);
  const refreshTimer = useRef(null);
  useEffect(() => { selectedRef.current = selectedId; }, [selectedId]);

  // The dashboard hands us a new addNotification on every one of its
  // renders (and it re-renders on every message). Keep it in a ref so
  // nothing below re-keys on it: a re-keyed loader once reset the open
  // thread the moment a message arrived.
  const notifyRef = useRef(addNotification);
  useEffect(() => { notifyRef.current = addNotification; }, [addNotification]);
  const notify = useCallback((title, message, bad = false) => {
    if (typeof notifyRef.current === 'function') notifyRef.current({ title, message, isEscalation: bad });
  }, []);

  // ----- list -----
  const loadThreads = useCallback(async (silent = false) => {
    if (!storeId) return;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_threads')
        .select('*')
        .eq('store_id', storeId)
        .order('last_activity_at', { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      setThreads(data || []);
    } catch (e) {
      console.error('ChatTab: threads', e);
      notify('Chat did not load', e.message || 'Try again', true);
    } finally {
      setLoading(false);
    }
  }, [storeId, notify]);

  useEffect(() => {
    setSelectedId(null);
    setMessages([]);
    setReceipts([]);
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // ----- one thread -----
  const markRead = useCallback(async (contactId) => {
    try {
      await supabase.from('messages').update({ is_read: true })
        .eq('contact_id', contactId).eq('role', 'user').eq('is_read', false);
      await supabase.from('receipts').update({ unread_messages_count: 0 })
        .eq('contact_id', contactId).gt('unread_messages_count', 0);
      setThreads((prev) => prev.map((t) => (t.id === contactId ? { ...t, unread_count: 0 } : t)));
    } catch (e) {
      console.error('ChatTab: mark read', e);
    }
  }, []);

  const loadThread = useCallback(async (contactId, silent = false) => {
    if (!contactId) return;
    if (!silent) setLoadingThread(true);
    try {
      const [m, r] = await Promise.all([
        supabase.from('messages')
          .select('*') // includes `source` once sql/chat-crm/04 has run; '*' so the thread loads either way
          .eq('contact_id', contactId)
          .order('created_at', { ascending: true })
          .limit(500),
        supabase.from('receipts')
          .select('id, created_at, total, delivery_status, is_ai_handling, sms_opted_out, sms_consent, customer_phone, customer_name, salesperson, import_source')
          .eq('contact_id', contactId)
          .order('created_at', { ascending: false })
          .limit(50),
      ]);
      if (m.error) throw m.error;
      if (r.error) throw r.error;
      if (selectedRef.current !== contactId) return; // moved on
      setMessages(m.data || []);
      setReceipts(r.data || []);
      if ((m.data || []).some((x) => x.role === 'user' && x.is_read === false)) markRead(contactId);
    } catch (e) {
      console.error('ChatTab: thread', e);
      notify('Thread did not load', e.message || 'Try again', true);
    } finally {
      setLoadingThread(false);
    }
  }, [markRead, notify]);

  const openThread = (id) => {
    setSelectedId(id);
    setDraft('');
    setShowDetails(false);
    const t = threads.find((x) => x.id === id);
    setNotesDraft(t?.notes || '');
    loadThread(id);
  };

  useEffect(() => {
    if (listEndRef.current) listEndRef.current.scrollIntoView({ block: 'end' });
  }, [messages.length, selectedId]);

  // ----- live -----
  useEffect(() => {
    if (!storeId) return undefined;
    const channel = supabase
      .channel(`chat-tab-${storeId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `store_id=eq.${storeId}` }, (payload) => {
        const row = payload.new;
        if (row?.contact_id && row.contact_id === selectedRef.current) {
          setMessages((prev) => (prev.some((x) => x.id === row.id) ? prev : [...prev, row]));
          if (row.role === 'user') markRead(row.contact_id);
        }
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        refreshTimer.current = setTimeout(() => loadThreads(true), 800);
      })
      .subscribe();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      supabase.removeChannel(channel);
    };
  }, [storeId, loadThreads, markRead]);

  // ----- derived -----
  const selected = useMemo(() => threads.find((t) => t.id === selectedId) || null, [threads, selectedId]);
  const latestReceipt = receipts[0] || null;
  const humanMode = latestReceipt ? latestReceipt.is_ai_handling === false : selected?.latest_ai_handling === false;
  const stopped = !!(selected?.sms_opted_out || latestReceipt?.sms_opted_out);
  // Where a reply goes: the way the customer last spoke. A website chat
  // (source 'website', mirrored from eastbay.direct's list page) is answered
  // on the website; anything else is a text.
  const lastFromCustomer = [...messages].reverse().find((m) => m.role === 'user');
  const channel = lastFromCustomer?.source === 'website' ? 'website' : 'sms';
  const canText = !!selected?.phone && !!(latestReceipt?.id || selected?.latest_receipt_id) && !stopped;
  const canReply = channel === 'website' ? true : canText;

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const nd = digits(q);
    return threads.filter((t) => {
      if (filter === 'unread' && !(t.unread_count > 0)) return false;
      if (filter === 'human' && t.latest_ai_handling !== false) return false;
      if (filter === 'online' && !['online_order', 'website_chat', 'sales_desk'].includes(t.first_source)) return false;
      if (filter === 'stop' && !t.sms_opted_out) return false;
      if (!needle) return true;
      if ((t.name || '').toLowerCase().includes(needle)) return true;
      if ((t.email || '').toLowerCase().includes(needle)) return true;
      if (nd && digits(t.phone).includes(nd)) return true;
      return (t.last_message || '').toLowerCase().includes(needle);
    });
  }, [threads, filter, q]);

  const unreadTotal = useMemo(() => threads.reduce((n, t) => n + (t.unread_count || 0), 0), [threads]);

  // ----- actions -----
  const send = async () => {
    const text = draft.trim();
    if (!text || !selected || sending) return;
    if (channel === 'website') {
      // A website reply is a row the site picks up (source 'website', no
      // external_ref yet) and shows on the customer's phone page and the
      // sales desk. The live channel adds it to this thread.
      setSending(true);
      try {
        const { error } = await supabase.from('messages').insert([{
          store_id: storeId, contact_id: selected.id, receipt_id: null,
          role: 'staff', content: text, sent_via: 'web', source: 'website',
          message_type: 'chat', is_read: true,
        }]);
        if (error) throw error;
        setDraft('');
        setTimeout(() => loadThread(selected.id, true), 600);
      } catch (e) {
        notify('Reply did not save', e.message || 'Try again', true);
      } finally {
        setSending(false);
      }
      return;
    }
    const receiptId = latestReceipt?.id || selected.latest_receipt_id;
    if (!receiptId) { notify('No receipt on file', 'Text replies need a receipt for this person.', true); return; }
    setSending(true);
    try {
      const res = await fetch('/api/sms/send-message', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          customerPhone: latestReceipt?.customer_phone || selected.latest_receipt_phone || selected.phone,
          message: text,
          receiptId,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'The text did not send');
      setDraft('');
      // The endpoint saved the staff message; the live channel usually
      // delivers it first, this is the fallback.
      setTimeout(() => loadThread(selected.id, true), 600);
    } catch (e) {
      notify('Text did not send', e.message || 'Try again', true);
    } finally {
      setSending(false);
    }
  };

  const toggleAi = async () => {
    if (!selected || togglingAi) return;
    const next = humanMode; // human → back to AI; AI → human
    setTogglingAi(true);
    try {
      const { error } = await supabase.from('receipts').update({ is_ai_handling: next }).eq('contact_id', selected.id);
      if (error) throw error;
      const receiptId = latestReceipt?.id || selected.latest_receipt_id;
      if (receiptId) {
        await sendMessage(receiptId, 'system', next ? 'Conversation returned to AI' : 'Staff has taken over the conversation', 'web');
      }
      setReceipts((prev) => prev.map((r) => ({ ...r, is_ai_handling: next })));
      setThreads((prev) => prev.map((t) => (t.id === selected.id ? { ...t, latest_ai_handling: next } : t)));
    } catch (e) {
      notify('Could not switch', e.message || 'Try again', true);
    } finally {
      setTogglingAi(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const { error } = await supabase.from('contacts').update({ notes: notesDraft.trim() || null }).eq('id', selected.id);
      if (error) throw error;
      setThreads((prev) => prev.map((t) => (t.id === selected.id ? { ...t, notes: notesDraft.trim() || null } : t)));
    } catch (e) {
      notify('Notes did not save', e.message || 'Try again', true);
    } finally {
      setSavingNotes(false);
    }
  };

  const openReceipt = (r) => {
    if (typeof onOpenReceipt === 'function' && r?.id) onOpenReceipt(r);
  };

  // ---------- render ----------
  const title = selected ? (selected.name || prettyPhone(selected.phone) || selected.email || 'Contact') : '';

  return (
    <div className="h-full min-h-0 flex flex-col bg-white md:rounded-2xl md:border md:border-slate-200 overflow-hidden">
      {!hideHeader && (
        <div className="flex-shrink-0 px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 leading-tight">
              Chat {unreadTotal > 0 && <span className="ml-2 inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white align-middle">{unreadTotal} new</span>}
            </h2>
            <p className="text-xs text-slate-500 truncate">Every customer in one place. Texts today, website chats next.</p>
          </div>
          <button type="button" onClick={() => loadThreads(true)} className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex">
        {/* ----- thread list ----- */}
        <aside className={`${selectedId ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 flex-shrink-0 flex-col border-r border-slate-200 min-h-0`}>
          <div className="flex-shrink-0 p-3 space-y-2 border-b border-slate-100">
            <label className="relative block">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, phone or words"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
              {q && (
                <button type="button" onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear search">
                  <X className="w-4 h-4" />
                </button>
              )}
            </label>
            <div className="flex gap-1 overflow-x-auto no-scrollbar">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold border transition ${filter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  {f.label}
                  {f.id === 'unread' && unreadTotal > 0 && <span className="ml-1 opacity-80">{unreadTotal}</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>
            ) : visible.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-600">{threads.length === 0 ? 'No conversations yet' : 'Nothing matches'}</p>
                <p className="text-xs text-slate-400 mt-1">{threads.length === 0 ? 'People appear here the first time they get a receipt or text the store.' : 'Try another filter or search.'}</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {visible.map((t) => {
                  const active = t.id === selectedId;
                  const who = t.name || prettyPhone(t.phone) || t.email || 'Contact';
                  const preview = t.last_message
                    ? `${t.last_role === 'staff' ? 'You: ' : t.last_role === 'assistant' ? 'AI: ' : t.last_role === 'system' ? '' : ''}${t.last_message}`
                    : 'No messages yet';
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => openThread(t.id)}
                        className={`w-full text-left px-3 py-3 flex gap-3 transition ${active ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${t.unread_count > 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                          {initials(who)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`truncate text-sm ${t.unread_count > 0 ? 'font-bold text-slate-900' : 'font-semibold text-slate-800'}`}>{who}</span>
                            <span className="ml-auto flex-shrink-0 text-[11px] text-slate-400">{timeAgo(t.last_message_at || t.last_activity_at)}</span>
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5">
                            <SourceChip source={t.first_source} />
                            {t.latest_ai_handling === false && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">You</span>}
                            {t.sms_opted_out && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">STOP</span>}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <p className={`truncate text-xs ${t.unread_count > 0 ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>{preview}</p>
                            {t.unread_count > 0 && (
                              <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{t.unread_count}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* ----- thread ----- */}
        <section className={`${selectedId ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 min-h-0 flex-col`}>
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mb-3"><MessageSquare className="w-7 h-7 text-slate-400" /></div>
              <p className="text-sm font-semibold text-slate-700">Pick a person</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">Their texts across every receipt, who is replying, and the receipts themselves, in one thread.</p>
            </div>
          ) : (
            <>
              {/* header */}
              <div className="flex-shrink-0 px-3 md:px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
                <button type="button" onClick={() => setSelectedId(null)} className="md:hidden -ml-1 p-1.5 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Back to list">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="truncate text-sm md:text-base font-bold text-slate-900">{title}</h3>
                    <SourceChip source={selected.first_source} className="hidden sm:inline-flex" />
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {selected.name && selected.phone ? prettyPhone(selected.phone) : ''}
                    {selected.receipt_count > 0 && <span>{selected.name && selected.phone ? ' · ' : ''}{selected.receipt_count} receipt{selected.receipt_count === 1 ? '' : 's'} · {money(selected.lifetime_total)}</span>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleAi}
                  disabled={togglingAi || (!latestReceipt && !selected.latest_receipt_id)}
                  title={humanMode ? 'You are replying. Tap to hand back to the AI.' : 'The AI is replying. Tap to take over.'}
                  className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition disabled:opacity-50 ${humanMode ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}
                >
                  {togglingAi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : humanMode ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{humanMode ? 'You are replying' : 'AI replying'}</span>
                  <span className="sm:hidden">{humanMode ? 'You' : 'AI'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className={`2xl:hidden flex-shrink-0 p-1.5 rounded-lg border ${showDetails ? 'bg-slate-900 text-white border-slate-900' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  aria-label="Details"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 min-h-0 flex">
                {/* messages + composer */}
                <div className={`${showDetails ? 'hidden 2xl:flex' : 'flex'} flex-1 min-w-0 min-h-0 flex-col bg-slate-50/60`}>
                  <div className="flex-1 min-h-0 overflow-y-auto px-3 md:px-5 py-4 space-y-2">
                    {loadingThread ? (
                      <div className="h-full flex items-center justify-center text-sm text-slate-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>
                    ) : messages.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center">
                        <p className="text-sm font-medium text-slate-600">No messages yet</p>
                        <p className="text-xs text-slate-400 mt-1">Send the first text below.</p>
                      </div>
                    ) : (
                      messages.map((m, i) => {
                        const prev = messages[i - 1];
                        const newDay = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
                        return (
                          <React.Fragment key={m.id || i}>
                            {newDay && (
                              <div className="flex items-center gap-3 py-2">
                                <div className="h-px flex-1 bg-slate-200" />
                                <span className="text-[11px] font-semibold text-slate-400">{dayLabel(m.created_at)}</span>
                                <div className="h-px flex-1 bg-slate-200" />
                              </div>
                            )}
                            <Bubble m={m} />
                          </React.Fragment>
                        );
                      })
                    )}
                    <div ref={listEndRef} />
                  </div>

                  {/* composer */}
                  <div className="flex-shrink-0 border-t border-slate-200 bg-white p-3">
                    {channel === 'website' ? null : stopped ? (
                      <p className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-center">They texted STOP. Texting stays off until they text START.</p>
                    ) : !selected.phone ? (
                      <p className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">No phone number on file for this person.</p>
                    ) : !(latestReceipt?.id || selected.latest_receipt_id) ? (
                      <p className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">No receipt yet. Texting from here comes with website chat, next.</p>
                    ) : null}
                    {(channel === 'website' || canText) && (
                      <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="flex items-end gap-2"
                      >
                        <textarea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                          rows={1}
                          placeholder={channel === 'website' ? `Reply on the website to ${selected.name || 'them'}` : humanMode ? `Text ${selected.name || prettyPhone(selected.phone)}` : 'Reply here. The AI keeps answering unless you take over.'}
                          className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-32"
                          style={{ minHeight: 42 }}
                        />
                        <button
                          type="submit"
                          disabled={!draft.trim() || sending || !canReply}
                          title={channel === 'website' ? 'Goes to their list page on the website' : 'Goes as a text'}
                          className={`flex-shrink-0 h-[42px] px-4 rounded-xl text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 ${channel === 'website' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : channel === 'website' ? <Globe className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                          <span className="hidden sm:inline">{channel === 'website' ? 'Reply on site' : 'Send'}</span>
                        </button>
                      </form>
                    )}
                  </div>
                </div>

                {/* details */}
                <aside className={`${showDetails ? 'flex' : 'hidden 2xl:flex'} w-full 2xl:w-72 flex-shrink-0 flex-col border-l border-slate-200 bg-white min-h-0 overflow-y-auto`}>
                  <div className="p-4 space-y-5 text-sm">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Person</p>
                      <p className="font-semibold text-slate-900">{selected.name || 'No name yet'}</p>
                      {selected.phone && <p className="text-slate-600">{prettyPhone(selected.phone)}</p>}
                      {selected.email && <p className="text-slate-600 truncate">{selected.email}</p>}
                      <p className="text-xs text-slate-400 mt-1">First seen {shortDate(selected.first_seen_at)} · <SourceChip source={selected.first_source} /></p>
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Texting</p>
                      <ConsentLine ok={selected.sms_service_ok} label="About their order" />
                      <ConsentLine ok={selected.sms_marketing_ok} label="About sales and deals" />
                      {selected.sms_opted_out && <p className="mt-1 text-xs font-semibold text-red-700">Texted STOP</p>}
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Receipts</p>
                      {receipts.length === 0 ? (
                        <p className="text-xs text-slate-400">None yet</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {receipts.map((r) => (
                            <li key={r.id}>
                              <button type="button" onClick={() => openReceipt(r)} className="w-full text-left rounded-lg border border-slate-200 px-2.5 py-2 hover:bg-slate-50 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span className="min-w-0 flex-1">
                                  <span className="block text-xs font-semibold text-slate-800">{shortDate(r.created_at)} · {money(r.total)}</span>
                                  <span className="block text-[11px] text-slate-500">{STATUS_LABEL[r.delivery_status] || r.delivery_status || 'Open'}{(r.import_source === 'eastbay' || r.salesperson === 'eastbay.direct') ? ' · Online order' : ''}</span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Notes</p>
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        rows={3}
                        placeholder="What they want, what was promised"
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {(notesDraft || '') !== (selected.notes || '') && (
                        <button type="button" onClick={saveNotes} disabled={savingNotes} className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                          {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                        </button>
                      )}
                    </div>
                  </div>
                </aside>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const ConsentLine = ({ ok, label }) => (
  <p className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-700' : 'text-slate-400'}`}>
    {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {label}{ok ? '' : ' (no)'}
  </p>
);

// Same sides as the Orders drawer: the customer on the right in blue, the
// store (AI or staff) on the left.
const Bubble = ({ m }) => {
  if (m.role === 'system' || m.message_type === 'receipt_sent') {
    return (
      <div className="flex justify-center">
        <span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-[11px] text-slate-500">
          {m.message_type === 'receipt_sent' ? 'Receipt sent' : m.content} · {clock(m.created_at)}
        </span>
      </div>
    );
  }
  const mine = m.role === 'user';
  const staff = m.role === 'staff';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[82%] md:max-w-[70%]">
        <div className={`px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap break-words ${mine ? 'bg-blue-600 text-white rounded-br-md' : staff ? 'bg-blue-100 border border-blue-200 text-slate-900 rounded-bl-md' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'}`}>
          {!mine && (
            <p className={`text-[11px] font-semibold mb-0.5 flex items-center gap-1 ${staff ? 'text-blue-700' : 'text-slate-500'}`}>
              {staff ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
              {staff ? (m.source === 'sales_desk' ? 'Sales desk' : m.source === 'website' ? 'Staff · website' : 'Staff') : m.message_type === 'survey' ? 'AI · survey' : m.message_type === 'phone_call_summary' ? 'Phone call' : 'AI'}
            </p>
          )}
          <Linkified text={m.content} light={mine} />
        </div>
        <p className={`text-[10px] text-slate-400 mt-0.5 ${mine ? 'text-right pr-1' : 'pl-1'}`}>{clock(m.created_at)}{m.source === 'website' || m.source === 'sales_desk' ? ' · Website' : m.sent_via ? ` · ${viaLabel(m.sent_via)}` : ''}</p>
      </div>
    </div>
  );
};

const Linkified = ({ text, light }) => {
  const parts = String(text || '').split(/(https?:\/\/\S+)/g);
  return (
    <>
      {parts.map((p, i) => (/^https?:\/\//.test(p)
        ? <a key={i} href={p} target="_blank" rel="noreferrer" className={`underline break-all ${light ? 'text-blue-100' : 'text-blue-700'}`}>{p}</a>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
};
